import { PayloadAction } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectToken } from 'core/selectors/product/token';
import { systemSigninRequest } from 'core/services/authentication';
import { dialogueRequest, tokenRequest } from 'core/services/product';
import { DialogueRegisterState, IHistoryItem, queue } from 'core/slices/register';
import { ISessionReducer, IToken, TokenErrorStatus, updateToken, updateTokenState } from 'core/slices/session';
import { eventChannel } from 'redux-saga';
import { all, fork, call, take, put, takeEvery, select } from 'redux-saga/effects';
import { RootState } from 'types';
import { LoadingState } from 'types/domain/base';
import { CourseNodeStatus, CourseNodeType, ICourseNodeDAO } from 'types/domain/course';
import { ISession } from 'types/domain/session';
import { validateEmail } from 'utils/validateEmail';

function* queueSaga(action: PayloadAction<IHistoryItem>) {
  const token: IToken = yield select(selectToken);
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  const item: IActionFeedItem = actionFeed.feed[actionFeed.index];
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);

  if (!action.payload.courseNodeId || !token?.scorm) return;

  const courseNode: ICourseNodeDAO = yield select((state: RootState) =>
    selectCourseNode(state, action.payload.courseNodeId),
  );

  if (courseNode.type === CourseNodeType.END) {
    const message = JSON.stringify({
      type: 'complete',
    });
    if (window.opener) window.opener.postMessage(message, '*');
    if (window.parent) window.parent.postMessage(message, '*');
    return;
  }

  //if (courseNode.status !== CourseNodeStatus.IDENTITY) return;

  const data = yield connectScorm();



  yield put(
    yield call(dialogueRequest, {
      courseProgressId,
      courseNodeId: courseNode.id,
      ...data,
      state: DialogueRegisterState.SUBMITTED,
      autoNext: true,
    }),
  );
}

function* tokenRequestSaga(action) {
  if (action.payload.scorm) {
    const data = yield connectScorm();

    console.log(data);

    if (!data)
      return yield put(
        yield call(updateTokenState, {
          token: action.payload.token,
          loading: LoadingState.ERROR,
          message: 'Could not connect to LMS',
        }),
      );

    const session: ISessionReducer = yield select((state: RootState) => state.session);

    yield put(
      yield call(tokenRequest, {
        ...data,
        token: action.payload.token,
        jwt: session.jwt,
        refresh: session.refresh,
        sessionId: session.session?.id,
        version: process.env.REACT_APP_VERSION,
      }),
    );
  }
}

function* connectScorm() {
  console.log('connectScorm()');
  const events = yield call(scormEvents);

  const message = JSON.stringify({
    type: 'initialize',
  });

  if (!window.parent && !window.opener) return console.warn('no parent or opener');

  if (window.parent) window.parent.postMessage(message, '*');
  if (window.opener) window.opener.postMessage(message, '*');

  let waiting = true;

  while (waiting) {
    const event = yield take(events);

    if (event.type === 'message')
      try {
        if (typeof event.data !== 'string') return;

        const { type, data } = JSON.parse(event.data);

        switch (type) {
          case 'authenticate':
            if (!data) return;

            waiting = false;

            const { name, systemId } = data;

            if (!name) return;

            const [lastName, firstName] = name.split(',');
            return {
              firstName,
              lastName,
              systemId,
              email: validateEmail(systemId) ? systemId : null,
            };
        }
      } catch (e) {
        console.log(e);
      }
  }
}

function waitForResponse() {}

export function* watchTokenResponse() {
  yield takeEvery(updateToken, tokenRequestSaga);
}

export function* watchQueue() {
  yield takeEvery([queue], queueSaga);
}

export function* scormSaga() {
  yield all([fork(watchQueue), fork(watchTokenResponse)]);
}

function scormEvents() {
  return eventChannel(emitter => {
    window.addEventListener('message', emitter);
    return () => window.removeEventListener('message', emitter, false);
  });
}
